import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  CompanyVendorDto,
  ContactDto,
  ContactsFindPrimaryContactTypeByVendorIdRequestDto,
  InsuranceAgentDto,
  InsuranceAgentsCreateRequestDto,
  InsuranceAgentsUpdateRequestDto,
  LicenseDto,
  LicenseSummaryResponseDto,
  LicensesGetPresignedUrlResponseDto,
  LicensesUpdateOneRequestDto,
  NotesFindAllVendorsRequestDto,
  NotesFindAllVendorsResponseDto,
  OwnerDto,
  OwnersUpsertRequestDto,
  SubNationalDivisionDto,
  SubscriptionDto,
  TradeLicensesBulkCreateRequestDto,
  TradeLicensesCoverageResponseDto,
  UserDto,
  UsersCreateForVendorRequestDto,
  UsersFindAllForVendorsRequestDto,
  UsersFindAllResponseDto,
  UsersUpdateOneRequestDto,
  VendorDto,
  VendorPaymentsGenerateReceiptRequestDto,
  VendorTradeDto,
  VendorTradesUpsertRequestDto,
  VendorsAdminRenewalSummaryResponseDto,
  VendorsAggregatedComplianceResponseDto,
  VendorsCompleteRenewalRequestDto,
  VendorsControllerFindRecommendedCompaniesV3Params,
  VendorsControllerFindServiceAreaSubNationalDivisionsV3Params,
  VendorsCreateTicketCommentRequestDto,
  VendorsFindAllRequestDto,
  VendorsFindAllResponseDto,
  VendorsFindProposalsRequestDto,
  VendorsFindProposalsResponseDto,
  VendorsFindTicketsRequestDto,
  VendorsGetLogoPresignedUrlResponseDto,
  VendorsIncompleteRegistrationsResponseDto,
  VendorsMissingAgreementsReportResponseDto,
  VendorsPaymentRequirementsResponseDto,
  VendorsPendingBidResponseDto,
  VendorsPortalRoutingResponseDto,
  VendorsRecommendedCompanyResponseDto,
  VendorsReconcileRenewalDatesRequestDto,
  VendorsReconcileRenewalDatesResponseDto,
  VendorsRenewalIntentRequestDto,
  VendorsRenewalIntentResponseDto,
  VendorsRenewalPolicyComplianceResponseDto,
  VendorsRenewalStatusResponseDto,
  VendorsStrandedRenewalReportResponseDto,
  VendorsTicketCommentResponseDto,
  VendorsTicketResponseDto,
  VendorsUpdateOneRequestDto,
  VendorsUpdateSubscriptionRequestDto,
  VendorsUpsertSubscriptionsDto,
  VendorsUpsertSubscriptionsResponseDto,
  VivePaymentMethodDto,
  ZendeskTicketCommentDto
} from './vive-api-models';

class VendorsService extends ViveApiBaseService {
  public async findAll(params: {
    options?: ViveApiRequestOptions;
    queryParams?: Omit<VendorsFindAllRequestDto, 'includeAssociations'>;
  }): Promise<VendorsFindAllResponseDto> {
    const { options, queryParams } = params;
    return await this.sendGet<VendorsFindAllResponseDto>({
      apiEndpoint: 'vendors',
      options,
      queryParams
    });
  }

  public async findById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<VendorDto> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorDto>({
      apiEndpoint: `vendors/:vendorId`,
      options,
      pathParams
    });
  }

  public async updateOne(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    data: VendorsUpdateOneRequestDto;
  }): Promise<VendorDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<VendorDto>({
      apiEndpoint: `vendors/:vendorId`,
      options,
      pathParams,
      data
    });
  }

  public async findAllInsuranceAgents(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<InsuranceAgentDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<InsuranceAgentDto[]>({
      apiEndpoint: `vendors/:vendorId/insurance-agents`,
      options,
      pathParams
    });
  }

  public async findInsuranceAgentById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; insuranceAgentId: number };
  }): Promise<InsuranceAgentDto> {
    const { options, pathParams } = params;
    return await this.sendGet<InsuranceAgentDto>({
      apiEndpoint: `vendors/:vendorId/insurance-agents/:insuranceAgentId`,
      options,
      pathParams
    });
  }

  public async createInsuranceAgent(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    data: InsuranceAgentsCreateRequestDto;
  }): Promise<InsuranceAgentDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<InsuranceAgentDto>({
      apiEndpoint: `vendors/:vendorId/insurance-agents`,
      options,
      pathParams,
      data
    });
  }

  public async updateInsuranceAgent(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; insuranceAgentId: number };
    data: InsuranceAgentsUpdateRequestDto;
  }): Promise<InsuranceAgentDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<InsuranceAgentDto>({
      apiEndpoint: `vendors/:vendorId/insurance-agents/:insuranceAgentId`,
      options,
      pathParams,
      data
    });
  }

  public async archiveInsuranceAgent(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; insuranceAgentId: number };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: `vendors/:vendorId/insurance-agents/:insuranceAgentId`,
      options,
      pathParams
    });
  }

  public async findRenewals(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<CompanyVendorDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<CompanyVendorDto[]>({
      apiEndpoint: `vendors/:vendorId/renewals`,
      options,
      pathParams
    });
  }

  public async findIncompleteRegistrations(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<VendorsIncompleteRegistrationsResponseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorsIncompleteRegistrationsResponseDto[]>({
      apiEndpoint: `vendors/:vendorId/incomplete-registrations`,
      options,
      pathParams
    });
  }

  public async findAllVendorTrades(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<VendorTradeDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorTradeDto[]>({
      apiEndpoint: `vendors/:vendorId/vendor-trades`,
      options,
      pathParams
    });
  }

  public async findVendorTradeById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; vendorTradeId: number };
  }): Promise<VendorTradeDto> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorTradeDto>({
      apiEndpoint: `vendors/:vendorId/vendor-trades/:vendorTradeId`,
      options,
      pathParams
    });
  }

  public async createVendorTrade(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    data: VendorTradesUpsertRequestDto;
  }): Promise<VendorTradeDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<VendorTradeDto>({
      apiEndpoint: `vendors/:vendorId/vendor-trades`,
      options,
      pathParams,
      data
    });
  }

  public async updateVendorTrade(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; vendorTradeId: number };
    data: VendorTradesUpsertRequestDto;
  }): Promise<VendorTradeDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<VendorTradeDto>({
      apiEndpoint: `vendors/:vendorId/vendor-trades/:vendorTradeId`,
      options,
      pathParams,
      data
    });
  }

  public async deleteVendorTrade(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; vendorTradeId: number };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: `vendors/:vendorId/vendor-trades/:vendorTradeId`,
      options,
      pathParams
    });
  }

  // VPDW-2616: returns structured { succeeded, failed }
  public async upsertSubscriptions(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    data: VendorsUpsertSubscriptionsDto;
  }): Promise<VendorsUpsertSubscriptionsResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<VendorsUpsertSubscriptionsResponseDto>({
      apiEndpoint: `vendors/:vendorId/subscription`,
      options,
      pathParams,
      data
    });
  }

  // VPDW-2616: Payment Hub — per-CompanyVendor payment states + dashboard banner flag
  public async getPaymentRequirements(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<VendorsPaymentRequirementsResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorsPaymentRequirementsResponseDto>({
      apiEndpoint: `vendors/:vendorId/payment-requirements`,
      options,
      pathParams
    });
  }

  public async findAllLicenses(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<LicenseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<LicenseDto[]>({
      apiEndpoint: `vendors/:vendorId/licenses`,
      options,
      pathParams
    });
  }

  public async createLicense(params: {
    pathParams: { vendorId: number };
    file: File;
  }): Promise<LicenseSummaryResponseDto> {
    const { pathParams, file } = params;
    const formData = new FormData();
    formData.append('file', file);
    return await this.sendPost<LicenseSummaryResponseDto>({
      apiEndpoint: `vendors/:vendorId/licenses`,
      pathParams,
      data: formData
    });
  }

  public async archiveLicense(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; licenseId: number };
  }): Promise<LicenseDto> {
    const { options, pathParams } = params;
    return await this.sendPatch<LicenseDto>({
      apiEndpoint: `vendors/:vendorId/licenses/:licenseId`,
      options,
      pathParams
    });
  }

  public async reviewLicense(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; licenseId: number };
    data: LicensesUpdateOneRequestDto;
  }): Promise<LicenseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<LicenseDto>({
      apiEndpoint: `vendors/:vendorId/licenses/:licenseId/review`,
      options,
      pathParams,
      data
    });
  }

  public async deleteLicense(params: {
    pathParams: { vendorId: number; licenseId: number };
  }): Promise<void> {
    const { pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: `vendors/:vendorId/licenses/:licenseId`,
      pathParams
    });
  }

  public async getLicensePresignedUrl(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; licenseId: number };
  }): Promise<LicensesGetPresignedUrlResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<LicensesGetPresignedUrlResponseDto>({
      apiEndpoint: 'vendors/:vendorId/licenses/:licenseId/presigned-url',
      options,
      pathParams
    });
  }

  public async getLogoPresignedUrl(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<VendorsGetLogoPresignedUrlResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorsGetLogoPresignedUrlResponseDto>({
      apiEndpoint: 'vendors/:vendorId/logo/presigned-url',
      options,
      pathParams
    });
  }

  public async downloadLicenseFile(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; licenseId: number };
  }): Promise<Blob> {
    const { options, pathParams } = params;
    return await this.sendGet<Blob>({
      apiEndpoint: 'vendors/:vendorId/licenses/:licenseId/file',
      options,
      pathParams
    });
  }

  public async bulkCreateTradeLicenses(params: {
    pathParams: { vendorId: number };
    data: TradeLicensesBulkCreateRequestDto;
  }): Promise<object> {
    const { pathParams, data } = params;
    return await this.sendPost<object>({
      apiEndpoint: `vendors/:vendorId/trade-licenses`,
      pathParams,
      data
    });
  }

  public async getTradeLicenseCoverage(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<TradeLicensesCoverageResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<TradeLicensesCoverageResponseDto>({
      apiEndpoint: `vendors/:vendorId/trade-licenses/coverage`,
      options,
      pathParams
    });
  }

  public async findAllOwners(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<OwnerDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<OwnerDto[]>({
      apiEndpoint: `vendors/:vendorId/owners`,
      options,
      pathParams
    });
  }

  public async findOwnerById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; ownerId: number };
  }): Promise<OwnerDto> {
    const { options, pathParams } = params;
    return await this.sendGet<OwnerDto>({
      apiEndpoint: `vendors/:vendorId/owners/:ownerId`,
      options,
      pathParams
    });
  }

  public async createOwner(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    data: OwnersUpsertRequestDto;
  }): Promise<OwnerDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<OwnerDto>({
      apiEndpoint: `vendors/:vendorId/owners`,
      options,
      pathParams,
      data
    });
  }

  public async updateOwner(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; ownerId: number };
    data: OwnersUpsertRequestDto;
  }): Promise<OwnerDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<OwnerDto>({
      apiEndpoint: `vendors/:vendorId/owners/:ownerId`,
      options,
      pathParams,
      data
    });
  }

  public async deleteOwner(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; ownerId: number };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: `vendors/:vendorId/owners/:ownerId`,
      options,
      pathParams
    });
  }

  public async deleteContact(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; contactId: number };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: `vendors/:vendorId/contacts/:contactId`,
      options,
      pathParams
    });
  }

  public async findServiceAreaSubNationalDivisions(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams?: VendorsControllerFindServiceAreaSubNationalDivisionsV3Params;
  }): Promise<SubNationalDivisionDto[]> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<SubNationalDivisionDto[]>({
      apiEndpoint: `vendors/:vendorId/service-area-states`,
      options,
      pathParams,
      // Spread, not cast. A generated DTO is an INTERFACE and so has no
      // implicit index signature; the anonymous object a spread produces does.
      // Guarded so an absent query stays absent — `{ ...undefined }` is `{}`,
      // which is truthy and would take the request builder's query branch.
      queryParams: queryParams && { ...queryParams }
    });
  }

  public async findRecommendedCompanies(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams?: VendorsControllerFindRecommendedCompaniesV3Params;
  }): Promise<VendorsRecommendedCompanyResponseDto[]> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<VendorsRecommendedCompanyResponseDto[]>({
      apiEndpoint: `vendors/:vendorId/recommended-companies`,
      options,
      pathParams,
      queryParams: queryParams && { ...queryParams }
    });
  }

  public async getPortalRouting(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<VendorsPortalRoutingResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorsPortalRoutingResponseDto>({
      apiEndpoint: `vendors/:vendorId/portal-routing`,
      options,
      pathParams
    });
  }

  public async findAllUsersForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams?: UsersFindAllForVendorsRequestDto;
  }): Promise<UsersFindAllResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<UsersFindAllResponseDto>({
      apiEndpoint: `vendors/:vendorId/users`,
      options,
      pathParams,
      queryParams: queryParams && { ...queryParams }
    });
  }

  public async createUserForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    data: UsersCreateForVendorRequestDto;
  }): Promise<UserDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<UserDto>({
      apiEndpoint: `vendors/:vendorId/users`,
      options,
      pathParams,
      data
    });
  }

  public async findUserForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; userId: number };
  }): Promise<UserDto> {
    const { options, pathParams } = params;
    return await this.sendGet<UserDto>({
      apiEndpoint: `vendors/:vendorId/users/:userId`,
      options,
      pathParams
    });
  }

  public async updateUserForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; userId: number };
    data: UsersUpdateOneRequestDto;
  }): Promise<UserDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<UserDto>({
      apiEndpoint: `vendors/:vendorId/users/:userId`,
      options,
      pathParams,
      data
    });
  }

  public async deleteUserForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; userId: number };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: `vendors/:vendorId/users/:userId`,
      options,
      pathParams
    });
  }

  public async getPaymentMethods(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<VivePaymentMethodDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<VivePaymentMethodDto[]>({
      apiEndpoint: `vendors/:vendorId/payment-methods`,
      options,
      pathParams
    });
  }

  public async deletePaymentMethod(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; paymentMethodId: string };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: `vendors/:vendorId/payment-methods/:paymentMethodId`,
      options,
      pathParams
    });
  }

  // VPDW-3200: make a payment method the default backing the vendor's renewals
  public async setDefaultPaymentMethod(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; paymentMethodId: string };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendPost<void>({
      apiEndpoint: `vendors/:vendorId/payment-methods/:paymentMethodId/default`,
      options,
      pathParams
    });
  }

  public async getSubscriptions(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<SubscriptionDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<SubscriptionDto[]>({
      apiEndpoint: `vendors/:vendorId/subscriptions`,
      options,
      pathParams
    });
  }

  public async generateReceipt(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    data: VendorPaymentsGenerateReceiptRequestDto;
  }): Promise<Blob> {
    const { options, pathParams, data } = params;
    return await this.sendPost<Blob>({
      apiEndpoint: 'vendors/:vendorId/payments/generate-receipt',
      options: { ...options, responseType: 'blob' },
      pathParams,
      data
    });
  }

  public async updateSubscription(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; subscriptionId: number };
    data: VendorsUpdateSubscriptionRequestDto;
  }): Promise<SubscriptionDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<SubscriptionDto>({
      apiEndpoint: `vendors/:vendorId/subscriptions/:subscriptionId`,
      options,
      pathParams,
      data
    });
  }

  public async findPrimaryContactTypeForVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams?: ContactsFindPrimaryContactTypeByVendorIdRequestDto;
  }): Promise<ContactDto> {
    const { pathParams, queryParams } = params;
    return await this.sendGet<ContactDto>({
      apiEndpoint: `vendors/:vendorId/contacts/primary-type`,
      pathParams,
      queryParams: queryParams && { ...queryParams }
    });
  }

  public async findTicketsByVendorId(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams?: Omit<VendorsFindTicketsRequestDto, 'includeAssociations'>;
  }): Promise<VendorsTicketResponseDto[]> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<VendorsTicketResponseDto[]>({
      apiEndpoint: `vendors/:vendorId/tickets`,
      options,
      pathParams,
      queryParams
    });
  }

  public async findTicketCommentsByTicketId(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; ticketId: number };
  }): Promise<VendorsTicketCommentResponseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorsTicketCommentResponseDto[]>({
      apiEndpoint: `vendors/:vendorId/tickets/:ticketId/comments`,
      options,
      pathParams
    });
  }

  public async createTicketComment(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; ticketId: number };
    data: VendorsCreateTicketCommentRequestDto;
  }): Promise<ZendeskTicketCommentDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<ZendeskTicketCommentDto>({
      apiEndpoint: `vendors/:vendorId/tickets/:ticketId/comments`,
      options,
      pathParams,
      data
    });
  }

  public async findProposalsByVendorId(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams?: Omit<VendorsFindProposalsRequestDto, 'includeAssociations'>;
  }): Promise<VendorsFindProposalsResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<VendorsFindProposalsResponseDto>({
      apiEndpoint: `vendors/:vendorId/proposals`,
      options,
      pathParams,
      queryParams
    });
  }

  public async findPendingBidsByVendorId(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
  }): Promise<VendorsPendingBidResponseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<VendorsPendingBidResponseDto[]>({
      apiEndpoint: `vendors/:vendorId/pending-bids`,
      options,
      pathParams
    });
  }

  public async findAllNotesByVendorId(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    queryParams?: Omit<NotesFindAllVendorsRequestDto, 'includeAssociations'>;
  }): Promise<NotesFindAllVendorsResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<NotesFindAllVendorsResponseDto>({
      apiEndpoint: `vendors/:vendorId/notes`,
      options,
      pathParams,
      queryParams
    });
  }
  // ── Vendor-Level Renewal ──

  public async getRenewalStatus(params: {
    pathParams: { vendorId: number };
  }): Promise<VendorsRenewalStatusResponseDto> {
    const { pathParams } = params;
    return await this.sendGet<VendorsRenewalStatusResponseDto>({
      apiEndpoint: `vendors/:vendorId/renewal-status`,
      pathParams
    });
  }

  /**
   * VPDW-5444: `data.statement` is what the vendor confirmed on the summary.
   * Optional; a completion without one is recorded with a null statement.
   */
  public async completeRenewal(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number };
    data?: VendorsCompleteRenewalRequestDto;
  }): Promise<VendorsRenewalStatusResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<VendorsRenewalStatusResponseDto>({
      apiEndpoint: `vendors/:vendorId/renewal-complete`,
      options,
      pathParams,
      data
    });
  }

  /**
   * VPDW-4034: record whether the vendor is renewing with one client, from the
   * "Your clients" step of their Annual Business Profile Update. Declining
   * excuses the relationship from `requiresSetup`, so the renewal can complete
   * while the vendor pays only for the clients they kept.
   */
  public async setRenewalIntent(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorId: number; companyVendorId: number };
    data: VendorsRenewalIntentRequestDto;
  }): Promise<VendorsRenewalIntentResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<VendorsRenewalIntentResponseDto>({
      apiEndpoint: `vendors/:vendorId/company-vendors/:companyVendorId/renewal-intent`,
      options,
      pathParams,
      data
    });
  }

  public async getAggregatedComplianceSettings(params: {
    pathParams: { vendorId: number };
  }): Promise<VendorsAggregatedComplianceResponseDto> {
    const { pathParams } = params;
    return await this.sendGet<VendorsAggregatedComplianceResponseDto>({
      apiEndpoint: `vendors/:vendorId/aggregated-compliance-settings`,
      pathParams
    });
  }

  public async getRenewalInsurancePolicyCompliance(params: {
    pathParams: { vendorId: number };
  }): Promise<VendorsRenewalPolicyComplianceResponseDto> {
    const { pathParams } = params;
    return await this.sendGet<VendorsRenewalPolicyComplianceResponseDto>({
      apiEndpoint: `vendors/:vendorId/renewal-insurance-policy-compliance`,
      pathParams
    });
  }

  public async getAdminRenewalSummary(): Promise<VendorsAdminRenewalSummaryResponseDto> {
    return await this.sendGet<VendorsAdminRenewalSummaryResponseDto>({
      apiEndpoint: `admin/vendor-renewal-summary`
    });
  }

  public async getAdminRenewals(params: {
    queryParams?: { status?: 'current' | 'dueSoon' | 'overdue' };
  }): Promise<VendorDto[]> {
    const { queryParams } = params;
    return await this.sendGet<VendorDto[]>({
      apiEndpoint: `admin/vendor-renewals`,
      // No conversion needed at all: this one's `queryParams` is declared as an
      // inline object type, and a type literal already carries an implicit
      // index signature. The cast here was never doing anything.
      queryParams
    });
  }

  public async getMissingAgreementsReport(params: {
    queryParams?: { limit?: number; afterId?: number };
  }): Promise<VendorsMissingAgreementsReportResponseDto> {
    const { queryParams } = params;
    return await this.sendGet<VendorsMissingAgreementsReportResponseDto>({
      apiEndpoint: `admin/missing-agreements-report`,
      queryParams
    });
  }

  public async reconcileRenewalDates(params: {
    options?: ViveApiRequestOptions;
    data: VendorsReconcileRenewalDatesRequestDto;
  }): Promise<VendorsReconcileRenewalDatesResponseDto> {
    const { options, data } = params;
    return await this.sendPost<VendorsReconcileRenewalDatesResponseDto>({
      apiEndpoint: `admin/vendor-renewal-date-reconciliation`,
      options,
      data
    });
  }

  public async getStrandedRenewalEffectsReport(params: {
    queryParams?: { limit?: number; afterId?: number };
  }): Promise<VendorsStrandedRenewalReportResponseDto> {
    const { queryParams } = params;
    return await this.sendGet<VendorsStrandedRenewalReportResponseDto>({
      apiEndpoint: `admin/stranded-renewal-effects-report`,
      queryParams
    });
  }
}

export const vendorsService = new VendorsService();
